import type { ComponentProps, ReactNode, Ref } from 'react'
import type { LucideIcon } from 'lucide-react'
import { cn } from '#core/utils/cn.ts'
import type { DataColor } from '#core/utils/data-colors.ts'

export type TooltipIndicatorShape = 'rounded' | 'circle' | 'line'

const SHAPE_CLASS: Record<TooltipIndicatorShape, string> = {
  rounded: 'ynt-tooltip-rounded',
  circle: 'ynt-tooltip-circle',
  line: 'ynt-tooltip-line',
}

const COLOR_CLASS: Record<DataColor, string> = {
  blue: 'ynt-tooltip-blue',
  red: 'ynt-tooltip-red',
  amber: 'ynt-tooltip-amber',
  green: 'ynt-tooltip-green',
  teal: 'ynt-tooltip-teal',
  purple: 'ynt-tooltip-purple',
  pink: 'ynt-tooltip-pink',
}

export interface TooltipIndicator {
  shape?: TooltipIndicatorShape
  color?: DataColor
  /** Raw CSS colour, for a swatch outside the {@link DataColor} ramp (e.g. a grey). Wins over `color`. */
  swatch?: string
}

export interface TooltipValue {
  label: ReactNode
  /** Trailing amount. Omit for a label-only row. */
  value?: ReactNode
  leadingIcon?: LucideIcon
  indicator?: TooltipIndicator
}

export interface TooltipPanelProps extends ComponentProps<'div'> {
  /** Header text. Omit for a headerless panel (value rows only). */
  label?: ReactNode
  /** Dimmed text beside the label, for a unit or qualifier. */
  secondary?: ReactNode
  leadingIcon?: LucideIcon
  values?: TooltipValue[]
  /** Portaled-panel chrome: stacking, no pointer events, enter animation. */
  floating?: boolean
  ref?: Ref<HTMLDivElement>
}

function Indicator({ shape = 'rounded', color = 'blue', swatch }: TooltipIndicator) {
  return (
    <span
      aria-hidden="true"
      className={cn('ynt-tooltip-indicator', SHAPE_CLASS[shape], swatch == null && COLOR_CLASS[color])}
      style={swatch != null ? { backgroundColor: swatch } : undefined}
    />
  )
}

function ValueRow({ label, value, leadingIcon: Icon, indicator }: TooltipValue) {
  return (
    <div className="ynt-tooltip-value">
      <span className="ynt-tooltip-value-main">
        {Icon && <Icon className="ynt-tooltip-icon" aria-hidden="true" />}
        {indicator && <Indicator {...indicator} />}
        <span className="ynt-tooltip-value-label">{label}</span>
      </span>
      {value && <span className="ynt-tooltip-value-amount">{value}</span>}
    </div>
  )
}

export function TooltipPanel({
  label,
  secondary,
  leadingIcon: Icon,
  values,
  floating = false,
  className,
  ref,
  ...rest
}: TooltipPanelProps) {
  const hasHeader = Icon != null || label != null || secondary != null
  return (
    <div ref={ref} className={cn('ynt-tooltip', floating && 'ynt-tooltip-floating', className)} {...rest}>
      {hasHeader && (
        <div className="ynt-tooltip-header">
          {Icon && <Icon className="ynt-tooltip-icon" aria-hidden="true" />}
          <span className="ynt-tooltip-labels">
            {label != null && <span>{label}</span>}
            {secondary && <span className="ynt-tooltip-secondary">{secondary}</span>}
          </span>
        </div>
      )}
      {values?.map((value, index) => (
        <ValueRow key={index} {...value} />
      ))}
    </div>
  )
}

export default TooltipPanel
